import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';
import { Input, Radio } from 'components/FormFields';
import FormModal, { FormGroup } from 'components/FormModal';
import TopicIcons from '../../LearningHub/components/TopicIcons';

const AVAILABLE_ICONS = [
  { key: 'sparkles', label: 'Sparkles' },
  { key: 'currency', label: 'Currency' },
  { key: 'heart', label: 'Heart' },
  { key: 'trophy', label: 'Trophy' },
  { key: 'rocket', label: 'Rocket' },
  { key: 'calendar', label: 'Calendar' },
  { key: 'ticket', label: 'Ticket' },
  { key: 'users', label: 'Users' },
  { key: 'device', label: 'Device' },
  { key: 'chart', label: 'Chart' },
  { key: 'megaphone', label: 'Megaphone' },
  { key: 'puzzle', label: 'Puzzle' },
];

const iconGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(4, 1fr)',
  gap: '8px',
  marginTop: '8px',
};

const iconBoxStyle = (isSelected) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '4px',
  padding: '10px 4px',
  borderRadius: '8px',
  border: isSelected ? '2px solid #FA4616' : '2px solid #e0e0e0',
  background: isSelected ? 'rgba(250, 70, 22, 0.06)' : 'transparent',
  cursor: 'pointer',
  transition: 'border-color 0.15s, background 0.15s',
});

const iconLabelStyle = {
  fontSize: '10px',
  color: '#666',
  textAlign: 'center',
  lineHeight: 1.2,
};

const TopicFormModal = ({ topic, onSave, onClose }) => {
  const [formData, setFormData] = useState(
    topic || {
      name: '',
      slug: '',
      description: '',
      mode: 'discover',
      icon: 'sparkles',
    }
  );

  // Auto-generate slug from name
  useEffect(() => {
    if (!topic && formData.name) {
      const slug = formData.name
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '');
      setFormData((prev) => ({ ...prev, slug }));
    }
  }, [formData.name, topic]);

  const handleSubmit = () => {
    onSave(formData);
  };

  return (
    <FormModal
      isOpen={true}
      onClose={onClose}
      title={topic ? 'Edit Topic' : 'Add New Topic'}
      subtitle="Organize videos into categories"
      submitText={topic ? 'Save Changes' : 'Add Topic'}
      onSubmit={handleSubmit}
      isSubmitting={false}
    >
      <FormGroup>
        <Radio
          name="mode"
          label="Mode *"
          value={formData.mode}
          onChange={(e) => setFormData({ ...formData, mode: e.target.value })}
          inline
          options={[
            { label: 'Ideas & Tips', value: 'discover' },
            { label: 'How To Do It', value: 'howto' },
          ]}
        />
      </FormGroup>

      <FormGroup>
        <Input
          label="Name"
          required
          id="name"
          name="name"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          placeholder="e.g., Getting Started"
        />
      </FormGroup>

      <FormGroup>
        <Input
          label="Slug"
          id="slug"
          name="slug"
          value={formData.slug}
          onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
          placeholder="auto-generated"
          helperText="URL-friendly identifier (auto-generated from name)"
          style={{ background: 'rgba(0,0,0,0.03)' }}
        />
      </FormGroup>

      <FormGroup>
        <Input
          label="Description"
          required
          id="description"
          name="description"
          value={formData.description}
          onChange={(e) =>
            setFormData({ ...formData, description: e.target.value })
          }
          placeholder="Brief description..."
        />
      </FormGroup>

      <FormGroup>
        {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
        <label
          style={{ display: 'block', fontSize: '14px', marginBottom: '4px' }}
        >
          Icon
        </label>
        <div style={iconGridStyle}>
          {AVAILABLE_ICONS.map(({ key, label }) => (
            <div
              key={key}
              style={iconBoxStyle(formData.icon === key)}
              onClick={() => setFormData({ ...formData, icon: key })}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setFormData({ ...formData, icon: key });
                }
              }}
            >
              <TopicIcons icon={key} size={26} />
              <span style={iconLabelStyle}>{label}</span>
            </div>
          ))}
        </div>
      </FormGroup>
    </FormModal>
  );
};

TopicFormModal.propTypes = {
  topic: PropTypes.object,
  onSave: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default TopicFormModal;
